import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";

import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import Typography from "@/components/ui/typography";
import {
  getOwnMentor,
  MentorsKeys,
  updateOwnMentorSettings,
} from "@/features/mentors/api/mentors";
import PayoutsSetupCard from "@/features/mentors/components/PayoutsSetupCard";
import { handleError } from "@/utils/error-handling";

const MentorSettings = () => {
  const formSchema = z.object({
    bio: z.union([
      z.literal(""),
      z.string().trim().max(1000, "Bio must be at most 1000 characters"),
    ]),
    calendarLink: z.union([
      z.literal(""),
      z.string().trim().url("Invalid URL"),
    ]),
  });

  type FormValues = z.infer<typeof formSchema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      bio: "",
      calendarLink: "",
    },
    mode: "onChange",
  });
  const queryClient = useQueryClient();
  const { data: me, isLoading: isLoadingMe } = useQuery({
    queryKey: [MentorsKeys.GET_ME],
    queryFn: getOwnMentor,
  });

  const { mutate: saveSettings, isPending } = useMutation({
    mutationFn: updateOwnMentorSettings,
    onSuccess: () => {
      toast.success("Settings saved");
      queryClient.invalidateQueries({ queryKey: [MentorsKeys.GET_ME] });
      // Reset dirty baseline to current values so the Save button disables
      const current = form.getValues();
      form.reset(current);
    },
    onError: handleError,
  });

  useEffect(() => {
    if (me) {
      form.reset({
        bio: me.bio ?? "",
        calendarLink: me.calendarLink ?? "",
      });
    }
  }, [me, form]);

  const onSubmit = (values: FormValues) => {
    saveSettings(values);
  };

  return (
    <DashboardLayout>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <Typography size="h1">Mentor Settings</Typography>
            <Typography size="b2" className="text-muted-foreground">
              Manage your preferences and account settings
            </Typography>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Profile Information */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>
                      <Typography size="h3">Profile Information</Typography>
                    </CardTitle>
                    <CardDescription>
                      <Typography size="b3">
                        Update your personal details and mentor profile
                      </Typography>
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  {isLoadingMe ? (
                    <Skeleton className="h-9 w-full" />
                  ) : (
                    <Input
                      id="firstName"
                      value={me?.firstName ?? ""}
                      disabled
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  {isLoadingMe ? (
                    <Skeleton className="h-9 w-full" />
                  ) : (
                    <Input id="lastName" value={me?.lastName ?? ""} disabled />
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  {isLoadingMe ? (
                    <Skeleton className="h-9 w-full" />
                  ) : (
                    <Input id="email" value={me?.email ?? ""} disabled />
                  )}
                </div>
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="bio"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Bio</FormLabel>
                        {isLoadingMe ? (
                          <Skeleton className="h-24 w-full" />
                        ) : (
                          <FormControl>
                            <Textarea
                              id="bio"
                              placeholder="Tell founders about your expertise..."
                              rows={3}
                              disabled={isPending}
                              {...field}
                            />
                          </FormControl>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isPending || isLoadingMe || !form.formState.isDirty}
                >
                  {isPending
                    ? "Saving..."
                    : isLoadingMe
                      ? "Loading..."
                      : "Save Settings"}
                </Button>
              </CardContent>
            </Card>

            {/* Communication Preferences */}
            <Card>
              <CardHeader>
                <CardTitle>
                  <Typography size="h3">Scheduling</Typography>
                </CardTitle>
                <CardDescription>
                  <Typography size="b3">
                    Add your calendar or scheduling link for founders to book
                    calls
                  </Typography>
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <FormField
                    control={form.control}
                    name="calendarLink"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Calendar Link</FormLabel>
                        {isLoadingMe ? (
                          <Skeleton className="h-9 w-full" />
                        ) : (
                          <FormControl>
                            <Input
                              id="calendarLink"
                              placeholder="https://calendly.com/your-link"
                              disabled={isPending}
                              {...field}
                            />
                          </FormControl>
                        )}
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Typography
                    size="l4"
                    className="text-muted-foreground"
                  ></Typography>
                </div>
              </CardContent>
            </Card>

            {/* Payouts Setup */}
            <div className="md:col-span-2">
              <PayoutsSetupCard mentor={me} />
            </div>
          </div>
        </form>
      </Form>
    </DashboardLayout>
  );
};

export default MentorSettings;
